/**
 * Button 元件
 *
 * 提供統一的按鈕樣式和行為：
 * - 支援多種變體（primary, secondary, danger, ghost）
 * - 支援載入狀態
 * - 支援禁用狀態
 */

// ========================================================================
// 型別定義
// ========================================================================

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonOptions {
  /** 按鈕變體 */
  variant?: ButtonVariant;
  /** 按鈕大小 */
  size?: ButtonSize;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否載入中 */
  loading?: boolean;
  /** 額外的 CSS 類別 */
  className?: string;
  /** 點擊事件處理器 */
  onClick?: (event: MouseEvent) => void;
}

// ========================================================================
// 樣式映射
// ========================================================================

const VARIANT_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-primary-600 hover:bg-primary-700 text-white border-transparent',
  secondary: 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300',
  danger: 'bg-red-600 hover:bg-red-700 text-white border-transparent',
  ghost: 'bg-transparent hover:bg-gray-100 text-gray-600 border-transparent',
};

const SIZE_STYLES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

const BASE_STYLES = [
  'inline-flex',
  'items-center',
  'justify-center',
  'font-medium',
  'rounded-lg',
  'border',
  'transition-colors',
  'duration-200',
  'focus:outline-none',
  'focus:ring-2',
  'focus:ring-offset-2',
  'focus:ring-primary-500',
].join(' ');

const DISABLED_STYLES = 'opacity-50 cursor-not-allowed';

// ========================================================================
// Button 類別
// ========================================================================

export class Button {
  private element: HTMLButtonElement;
  private options: Required<Omit<ButtonOptions, 'onClick'>> & Pick<ButtonOptions, 'onClick'>;

  constructor(text: string, options: ButtonOptions = {}) {
    this.options = {
      variant: options.variant ?? 'primary',
      size: options.size ?? 'md',
      disabled: options.disabled ?? false,
      loading: options.loading ?? false,
      className: options.className ?? '',
      onClick: options.onClick,
    };

    this.element = document.createElement('button');
    this.element.type = 'button';
    this.setText(text);
    this.applyStyles();
    this.bindEvents();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  /**
   * 取得 DOM 元素
   */
  getElement(): HTMLButtonElement {
    return this.element;
  }

  /**
   * 設定按鈕文字
   */
  setText(text: string): void {
    if (this.options.loading) {
      this.element.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        ${text}
      `;
    } else {
      this.element.textContent = text;
    }
  }

  /**
   * 設定載入狀態
   */
  setLoading(loading: boolean): void {
    this.options.loading = loading;
    this.options.disabled = loading;
    this.applyStyles();
    this.setText(this.element.textContent ?? '');
  }

  /**
   * 設定禁用狀態
   */
  setDisabled(disabled: boolean): void {
    this.options.disabled = disabled;
    this.applyStyles();
  }

  /**
   * 掛載到指定容器
   */
  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  /**
   * 從 DOM 移除
   */
  destroy(): void {
    this.element.remove();
  }

  // ========================================================================
  // 私有方法
  // ========================================================================

  private applyStyles(): void {
    const classes = [
      BASE_STYLES,
      VARIANT_STYLES[this.options.variant],
      SIZE_STYLES[this.options.size],
      this.options.disabled ? DISABLED_STYLES : '',
      this.options.className,
    ].filter(Boolean).join(' ');

    this.element.className = classes;
    this.element.disabled = this.options.disabled;
  }

  private bindEvents(): void {
    if (this.options.onClick) {
      this.element.addEventListener('click', this.options.onClick);
    }
  }
}

// ========================================================================
// 工廠函式
// ========================================================================

/**
 * 建立主要按鈕
 */
export function createPrimaryButton(text: string, onClick?: () => void): Button {
  return new Button(text, { variant: 'primary', onClick });
}

/**
 * 建立次要按鈕
 */
export function createSecondaryButton(text: string, onClick?: () => void): Button {
  return new Button(text, { variant: 'secondary', onClick });
}

/**
 * 建立危險操作按鈕
 */
export function createDangerButton(text: string, onClick?: () => void): Button {
  return new Button(text, { variant: 'danger', onClick });
}
